<template>
    <div class="container-fluid">
        <div class="row">
            <!-- Sidebar for Form Controls -->
            <div class="col-md-3 sidebar bg-light">
                <div class="info-panel p-3">
                    <h2 class="mb-4">Spatial Explorer</h2>

                    <!-- Climate Model Settings -->
                    <div class="control-group mb-4">
                        <h5 class="mb-3">Climate Model Settings</h5>
                        <div v-if="loading.climateModel" class="loading-container">
                            <div class="loading-spinner"></div>
                            <p class="loading-text">Climate model settings are loading...</p>
                        </div>
                        <div v-else>
                            <div class="mb-3">
                                <label class="form-label fw-bold" for="global-climate-model">Global Climate
                                    Model:</label>
                                <select v-model="selection.global" @change="handleGlobalModelChange"
                                    class="form-select" id="global-climate-model">
                                    <option v-for="model in available.globalModels" :key="model" :value="model">{{
                                        model }}
                                    </option>
                                </select>
                            </div>
                            <div class="mb-3">
                                <label class="form-label fw-bold" for="regional-climate-model">Regional
                                    Climate Model:</label>
                                <select v-model="selection.regional" @change="handleRegionalModelChange"
                                    class="form-select" id="regional-climate-model">
                                    <option v-for="model in available.regionalModels" :key="model" :value="model">
                                        {{ model }}
                                    </option>
                                </select>
                            </div>
                            <div class="mb-3">
                                <label class="form-label fw-bold" for="scenario-climate-model">RCP Scenario:</label>
                                <select v-model="selection.scenario" @change="handleScenarioChange"
                                    class="form-select" id="scenario-climate-model">
                                    <option v-for="scen in available.scenarios" :key="scen" :value="scen">{{
                                        scen }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <!-- Data Selection -->
                    <div class="control-group mb-4">
                        <h5 class="mb-3">Data Selection</h5>
                        <div v-if="loading.data" class="loading-container">
                            <div class="loading-spinner"></div>
                            <p class="loading-text">Data is loading...</p>
                        </div>
                        <div v-else>
                            <div class="mb-3">
                                <label class="form-label fw-bold" for="climate-variable">Climate Variable:</label>
                                <select v-model="selection.climateVar" @change="handleClimateVarChange"
                                    class="form-select" id="climate-variable">
                                    <option v-for="(name, key) in OPTION_CLIMA_VAR" :key="key" :value="key">{{ name
                                        }}
                                    </option>
                                </select>
                            </div>
                            <div class="mb-3">
                                <label class="form-label fw-bold" for="temporal_resolution">Temporal
                                    Resolution:</label>
                                <select v-model="selection.tempResolution" @change="handleTempResolutionChange"
                                    class="form-select" id="temporal_resolution">
                                    <option v-for="(name, key) in OPTIONS_TEMP_RESOLUTION" :key="key" :value="key">
                                        {{ name }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <!-- Location Analysis -->
                    <div class="control-group">
                        <h5 class="mb-3">Location Analysis</h5>
                        <div v-if="loading.visualization">
                            <p class="loading-text">Waiting for visualization...</p>
                        </div>
                        <div v-else>
                            <button class="btn btn-primary mb-3 w-100" :class="{ active: isSelectionMode }"
                                @click="toggleSelectionMode">
                                {{ isSelectionMode ? 'Select Position (active)' : 'Select Position' }}
                            </button>
                            <div v-if="showSubfilter" class="mb-3">
                                <label class="form-label fw-bold" for="season-selection">Subfilter:</label>
                                <select v-model="selection.subfilter" @change="handleSubfilterChange"
                                    class="form-select" id="season-selection">
                                    <option v-for="option in available.subfilters" :key="option.value"
                                        :value="option.value">{{ option.label }}</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <!-- Display Selected Model Info -->
                    <div v-if="selectedCollectionId" class="mt-3">
                        <p><strong>Selected Model:</strong></p>
                        <p class="small">{{ selectedCollectionId }}</p>
                    </div>
                </div>
            </div>

            <!-- Main Content: Map and Visualization -->
            <div class="col-md-9">
                <!-- Map -->
                <div id="map-container" ref="mapContainer">
                    <div id="map"></div>
                </div>

                <!-- Visualization Controls -->
                <div class="visualization-controls p-3" v-if="!loading.visualization">
                    <div class="row align-items-center">
                        <div class="col-md-2">
                            <label class="form-label fw-bold">Legend</label>
                            <div id="legend">
                                <div class="legend-gradient" :style="{ background: legendGradient }"></div>
                                <div class="d-flex justify-content-between">
                                    <span v-for="label in legendLabels" :key="label">{{ label }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="col-md-10">
                            <input v-model.number="currentDateIDX" type="range" class="form-range"
                                :max="timeStamps.length - 1" min="0" @input="handleSliderInput" />
                            <div class="d-flex justify-content-between">
                                <span class="small">{{ formattedStartDate }}</span>
                                <span class="small fw-bold">{{ formattedCurrentDate }}</span>
                                <span class="small">{{ formattedEndDate }}</span>
                            </div>
                        </div>
                    </div>
                </div>
                 <div v-else class="loading-container">
                    <div class="loading-spinner"></div>
                    <p class="loading-text">Visualization is loading...</p>
                </div>


                <!-- Graph Container -->
                <div class="graph-container" :class="{ open: isGraphOpen }">
                    <button v-if="isGraphOpen" @click="closeGraph" class="close-btn">×</button>
                    <div class="chart-container">
                        <canvas ref="chartCanvas"></canvas>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted, computed, watch, nextTick, defineProps } from 'vue';
import mapboxgl from 'mapbox-gl';
import Chart from 'chart.js/auto';
import 'chartjs-adapter-luxon';
import { DateTime } from 'luxon';

// --- CONSTANTS ---
const BASE_URL = "https://klimakonform-maps.geo.tu-dresden.de/pygeoapi";
const COLOR_SETTINGS = {
    temperature_mean: { min_max: [-5, 20], color_palette: ["#2166ac", "#f7f7f7", "#fddbc7", "#ef8a62", "#b2182b"] },
    temperature_min: { min_max: [-5, 20], color_palette: ["#2166ac", "#f7f7f7", "#fddbc7", "#ef8a62", "#b2182b"] },
    temperature_max: { min_max: [-5, 20], color_palette: ["#2166ac", "#f7f7f7", "#fddbc7", "#ef8a62", "#b2182b"] },
    precipitation: { min_max: [0, 5], color_palette: ["#d5d5d5", "#9bafd9", "#103783"] },
    wind: { min_max: [0, 20], color_palette: ["#FFFFFF", "#103783"] },
    humidity: { min_max: [65, 85], color_palette: ["#ff1b23", "#ffc5c8", "#ebeef4", "#c1deff", "#45abff"] },
    radiation: { min_max: [0, 2000], color_palette: ["#ffffff", "#fffbaf", "#f7ba2c", "#ea5459"] }
};
const OPTIONS_TEMP_RESOLUTION = { monthly: "Monthly", seasonal: "Seasonal", annual: "Annual", "30year": "30-Year Periods" };
const OPTION_CLIMA_VAR = { temperature_mean: "Mean Temperature 2m", temperature_min: "Maximum Temperature 2m", temperature_max: "Minimum Temperature 2m", precipitation: "Corrected Precipitation", wind: "Mean Wind Speed 10m", humidity: "Relative Humidity 2m", radiation: "Global Radiation" };
const UNIT_DICT = { Celsius: "°C", "1": "%", "W m-2": "W/m²", "m s-1": "m/s", "kg m-2": "kg/m²" };
const FILTER_OPTIONS = {
    monthly: { options: [{ value: 'false', label: 'No Filter' }, { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' }, { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' }, { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' }, { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }] },
    seasonal: { options: [{ value: 'false', label: 'No Filter' }, { value: 5, label: 'Spring' }, { value: 8, label: 'Summer' }, { value: 11, label: 'Autumn' }, { value: 2, label: 'Winter' }] }
};

const props = defineProps({
    isActive: {
        type: Boolean,
        default: false
    }
});

// Watch for isActive prop changes to resize the map
watch(() => props.isActive, (newVal) => {
    if (newVal && map) {
        // ensure DOM is rendered and the map container is visible before resizing
        // do multiple attempts: immediate nextTick + short timeouts to cover transitions
        nextTick(() => {
            try { map.resize(); } catch (e) { void e; }
            setTimeout(() => { try { map.resize(); } catch (e) { void e; } }, 80);
            setTimeout(() => { try { map.resize(); } catch (e) { void e; } }, 300);
        });
    }
});

// --- REFS & STATE ---
const mapContainer = ref(null);
const chartCanvas = ref(null);
let map = null;
let chart = null;
let mapMarker = null;
let inactivityTimeout = null;

// add a debounce timeout for resize events
let resizeTimeout = null;

const loading = reactive({
    climateModel: true,
    data: true,
    visualization: true
});

// helper: debounce window resize and call map.resize()
function handleWindowResize() {
    if (!map) return;
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
        try { map.resize(); } catch (e) { void e; /* swallow resize errors */ }
    }, 120);
}

// external notification (from parent) to force map resize after tab activation
function handleExternalMapResize() {
    if (!map) return;
    nextTick(() => {
        // small delay to allow tab transition / layout to settle
        setTimeout(() => { try { map.resize(); } catch (e) { void e; } }, 50);
    });
}

onUnmounted(() => {
    window.removeEventListener('resize', handleWindowResize);
    window.removeEventListener('map-resize', handleExternalMapResize);
    clearTimeout(resizeTimeout);
});

// --- REACTIVE DATA ---
const selection = reactive({
    global: '',
    regional: '',
    scenario: '',
    climateVar: 'temperature_mean',
    tempResolution: 'monthly',
    subfilter: 'false',
});

const available = reactive({
    globalModels: [],
    regionalModels: [],
    scenarios: [],
    subfilters: [],
});

let schemaData = {};
let parsedIds = [];
let selectedParameters = null;
let parameterUnitName = {};

const allRasterData = ref([]);
const allTimeStamps = ref([]);
const selectedCollectionId = ref('');

const timeStamps = computed(() => {
    if (selection.subfilter === 'false') {
        return allTimeStamps.value.map((timestamp, index) => ({ date: timestamp, originalIndex: index }));
    }

    const filterValue = parseInt(selection.subfilter, 10);
    return allTimeStamps.value.map((timestamp, index) => ({
        date: timestamp,
        originalIndex: index
    })).filter(item => {
        const date = new Date(item.date);
        if (selection.tempResolution === 'monthly') {
            return date.getMonth() + 1 === filterValue;
        } else if (selection.tempResolution === 'seasonal') {
            const month = date.getMonth() + 1;
            switch (filterValue) {
                case 5: return [3, 4, 5].includes(month); // Spring
                case 8: return [6, 7, 8].includes(month); // Summer
                case 11: return [9, 10, 11].includes(month); // Autumn
                case 2: return [12, 1, 2].includes(month); // Winter
                default: return false;
            }
        }
        return true;
    });
});
const currentDateIDX = ref(0);
const selectedPosition = ref(null); // { lat, lng, boundedRow, boundedCol }
const isSelectionMode = ref(false);
const isGraphOpen = ref(false);

// --- COMPUTED PROPERTIES ---
const bbox = computed(() => {
    if (!selectedParameters?.extent?.spatial?.bbox?.[0]) return null;
    const [minLng, minLat, maxLng, maxLat] = selectedParameters.extent.spatial.bbox[0];
    return [[minLat, minLng], [maxLat, maxLng]];
});

const mapBbox = computed(() => {
    if (!bbox.value) return null;
    return [[bbox.value[0][1], bbox.value[0][0]], [bbox.value[1][1], bbox.value[1][0]]];
});

const currentUnit = computed(() => {
    const unitInfo = parameterUnitName[selection.climateVar];
    return unitInfo ? UNIT_DICT[unitInfo.unit] || unitInfo.unit : '';
});

const positionDataValue = computed(() => {
    if (!selectedPosition.value || timeStamps.value.length === 0) return null;
    const { boundedRow, boundedCol } = selectedPosition.value;
    return allRasterData.value[timeStamps.value[currentDateIDX.value].originalIndex]?.[boundedRow]?.[boundedCol];
});

const timeSeriesData = computed(() => {
    if (!selectedPosition.value || timeStamps.value.length === 0) return [];

    const { boundedRow, boundedCol } = selectedPosition.value;
    return timeStamps.value.map((item) => {
        const date = new Date(item.date);
        return {
            x: date.getTime(),
            y: allRasterData.value[item.originalIndex][boundedRow][boundedCol],
        };
    });
});

// --- DATA PARSING (similar to first component) ---
const parseId = (id) => {
    // Exclude IDs with "KlimaKonform"
    if (id.includes('KlimaKonform')) {
        return null;
    }

    // Only include IDs starting with "MDK_SN_SA_TH"
    /* if (!id.startsWith('MDK_SN_SA_TH_')) {
        return null;
    } */

    const parts = id.split('_');
    
    // The pattern is MDK_SN_SA_TH_cmip5_<global model>_<regional model>_<RCP scenario>...
    // So we need at least 8 parts.
    if (parts.length >= 8) {
        // parts[0-3] are MDK_SN_SA_TH, parts[4] is cmip5
        const globalModel = parts[5];
        const regionalModel = parts[6];
        const rcpScenario = parts[7];
        return { id, globalModel, regionalModel, rcpScenario };
    }

    return null;
};

const availableGlobalModels = computed(() => {
    return [...new Set(parsedIds
        .map(id => id.globalModel))];
});

const availableRegionalModels = computed(() => {
    return [...new Set(parsedIds
        .filter(id =>
            (!selection.global || id.globalModel === selection.global)
        )
        .map(id => id.regionalModel))];
});

const availableRcpScenarios = computed(() => {
    return [...new Set(parsedIds
        .filter(id =>
            (!selection.global || id.globalModel === selection.global) &&
            (!selection.regional || id.regionalModel === selection.regional)
        )
        .map(id => id.rcpScenario))];
});

const selectedIDs = computed(() => {
    return parsedIds
        .filter(id =>
            (!selection.global || id.globalModel === selection.global) &&
            (!selection.regional || id.regionalModel === selection.regional) &&
            (!selection.scenario || id.rcpScenario === selection.scenario)
        )
        .map(id => id.id);
});

// --- DATE FORMATTING ---
const formatISODate = (isoDate, isCurrent = false) => {
    if (!isoDate) return '-';
    const date = DateTime.fromJSDate(new Date(isoDate));
    if (!isCurrent) return date.toFormat('yyyy');

    switch (selection.tempResolution) {
        case "monthly": return date.toFormat('LLLL yyyy');
        case "seasonal": {
            const month = date.month;
            const year = date.year;
            if ([12, 1, 2].includes(month)) return `Winter ${year}`;
            if ([3, 4, 5].includes(month)) return `Spring ${year}`;
            if ([6, 7, 8].includes(month)) return `Summer ${year}`;
            return `Autumn ${year}`;
        }
        case "annual": return date.toFormat('yyyy');
        case "30year": return `${date.year}-${date.year + 29}`;
        default: return date.toFormat('LLLL yyyy');
    }
};

const formattedStartDate = computed(() => formatISODate(timeStamps.value[0]?.date));
const formattedCurrentDate = computed(() => formatISODate(timeStamps.value[currentDateIDX.value]?.date, true));
const formattedEndDate = computed(() => formatISODate(timeStamps.value[timeStamps.value.length - 1]?.date));

// --- LEGEND ---
const legendGradient = computed(() => {
    const settings = COLOR_SETTINGS[selection.climateVar];
    if (!settings) return '';
    return `linear-gradient(to right, ${settings.color_palette.join(", ")})`
});

const legendLabels = computed(() => {
    const settings = COLOR_SETTINGS[selection.climateVar];
    if (!settings) return [];
    const [min, max] = settings.min_max;
    return [`${min}${currentUnit.value}`, `${(min + max) / 2}${currentUnit.value}`, `${max}${currentUnit.value}`];
});

// --- SUBFILTER ---
const showSubfilter = computed(() => ['monthly', 'seasonal'].includes(selection.tempResolution));
watch(() => selection.tempResolution, (newResolution) => {
    if (['monthly', 'seasonal'].includes(newResolution)) {
        available.subfilters = FILTER_OPTIONS[newResolution].options;
        selection.subfilter = 'false';
    } else {
        available.subfilters = [];
        selection.subfilter = 'false';
    }
}, { immediate: true });

watch(() => selection.subfilter, () => {
    currentDateIDX.value = 0;
    updateMap();
});

// --- DATA FETCHING & PROCESSING ---
async function loadInitialData() {
    try {
        const response = await fetch(`${BASE_URL}/collections?f=json`);
        schemaData = await response.json();
        
        // Process climate model data using the same pattern as first component
        if (schemaData.collections && schemaData.collections.length > 0) {
            parsedIds = schemaData.collections.map(c => parseId(c.id)).filter(id => id !== null);
        }

        populateModelSelections();
        await updateData(true); // Initial data load
    } catch (error) {
        console.error("Error loading initial data:", error);
    } finally {
        loading.climateModel = false;
        loading.data = false;
    }
}

function populateModelSelections() {
    available.globalModels = availableGlobalModels.value;
    available.regionalModels = availableRegionalModels.value;
    available.scenarios = availableRcpScenarios.value;

    if (available.globalModels.length > 0) {
        selection.global = available.globalModels[0];
    }
    if (available.regionalModels.length > 0) {
        selection.regional = available.regionalModels[0];
    }
    if (available.scenarios.length > 0) {
        selection.scenario = available.scenarios[0];
    }
}

async function updateData(isInitial = false) {
    loading.visualization = true;

    // Find the selected collection ID
    const matchingIds = selectedIDs.value;
    if (matchingIds.length === 0) {
        console.error('No matching collections found!');
        loading.visualization = false;
        return;
    }

    // Use the first matching ID
    const collectionId = matchingIds[0];
    selectedCollectionId.value = collectionId;
    
    selectedParameters = schemaData.collections.find(item => item.id === collectionId);

    if (!selectedParameters) {
        console.error(`Collection ${collectionId} not found!`);
        loading.visualization = false;
        return;
    }

    createParameterUnitName();

    const dataUrl = `${BASE_URL}/processes/AggregationTime/execution`;
    const paramsProcess = {
        inputs: {
            collection_id: collectionId,
            climate_indicator: selection.climateVar,
            temporal_aggregation: selection.tempResolution
        }
    };

    try {
        const response = await fetch(dataUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(paramsProcess)
        });
        let responseText = await response.text();
        responseText = responseText.replace(/\bNaN\b/g, "null");
        const data = JSON.parse(responseText);

        if (data && data.value && data.value.timestamps && data.value.aggregated_values) {
            allTimeStamps.value = data.value.timestamps.map(ns => new Date(ns / 1e6));
            allRasterData.value = data.value.aggregated_values;
        } else {
            console.error("Invalid data structure from server:", data);
            allTimeStamps.value = [];
            allRasterData.value = [];
        }
        currentDateIDX.value = 0;

        await nextTick();
        if (isInitial) {
            initializeMap();
        } else {
            updateMap(true);
        }
        if (isGraphOpen.value) {
            updateGraph();
        }

    } catch (error) {
        console.error("Error fetching data:", error);
    } finally {
        loading.visualization = false;
    }
}

function createParameterUnitName() {
    parameterUnitName = Object.entries(selectedParameters['parameter_names']).reduce((acc, [key, { name, unit }]) => {
        acc[key] = { name, unit: unit.symbol.value };
        return acc;
    }, {});
}

// --- UI HANDLERS ---
async function handleGlobalModelChange() {
    // Update available regional models and scenarios based on selected global model
    available.regionalModels = availableRegionalModels.value;
    available.scenarios = availableRcpScenarios.value;
    
    if (available.regionalModels.length > 0) {
        selection.regional = available.regionalModels[0];
    }
    if (available.scenarios.length > 0) {
        selection.scenario = available.scenarios[0];
    }
    await updateData();
}

async function handleRegionalModelChange() {
    // Update available scenarios based on selected models
    available.scenarios = availableRcpScenarios.value;
    if (available.scenarios.length > 0) {
        selection.scenario = available.scenarios[0];
    }
    await updateData();
}

async function handleScenarioChange() { await updateData(); }
async function handleClimateVarChange() { await updateData(); }
async function handleTempResolutionChange() { await updateData(); }

function handleSliderInput() {
    clearTimeout(inactivityTimeout);
    inactivityTimeout = setTimeout(() => updateMap(), 100);
}

function handleSubfilterChange() {
    if (isGraphOpen.value) {
        updateGraph();
    }
}

// --- MAP LOGIC ---
function initializeMap() {
    mapboxgl.accessToken = 'pk.eyJ1Ijoic2ltZW9udyIsImEiOiJjbTE3dThoMXowdDJ6Mm5zY3l6YnRwYnJqIn0.J9NB98kNT_vmotP-Lq4MQA';
    map = new mapboxgl.Map({
        container: 'map',
        style: 'mapbox://styles/mapbox/light-v11',
        center: [12.44, 51.08],
        zoom: 7
    });

    map.on('load', () => {
        if (timeStamps.value.length > 0) {
            map.addSource('raster-source', {
                type: 'image',
                url: createRasterImage(timeStamps.value[currentDateIDX.value].originalIndex),
                coordinates: mapBbox.value ? [
                    [mapBbox.value[0][0], mapBbox.value[1][1]], // NW
                    [mapBbox.value[1][0], mapBbox.value[1][1]], // NE
                    [mapBbox.value[1][0], mapBbox.value[0][1]], // SE
                    [mapBbox.value[0][0], mapBbox.value[0][1]]  // SW
                ] : [[0, 0], [1, 0], [1, 1], [0, 1]]
            });

            map.addLayer({
                id: 'raster-layer',
                type: 'raster',
                source: 'raster-source',
                paint: { 'raster-opacity': 0.6 }
            });
        }

        if (mapBbox.value) {
            map.fitBounds(mapBbox.value, { padding: 20 });
            // ensure map canvas is resized after fitBounds/layout
            nextTick(() => { try { map.resize(); } catch (e) { void e; /* swallow resize errors */ } });
        }
        
        map.on('click', onMapClick);
    });
}

function updateMap(fitBounds = false) {
    if (!map || !map.isStyleLoaded() || !timeStamps.value[currentDateIDX.value] || !mapBbox.value) return;

    const source = map.getSource('raster-source');
    if (source) {
        source.updateImage({
            url: createRasterImage(timeStamps.value[currentDateIDX.value].originalIndex),
            coordinates: [
                [mapBbox.value[0][0], mapBbox.value[1][1]],
                [mapBbox.value[1][0], mapBbox.value[1][1]],
                [mapBbox.value[1][0], mapBbox.value[0][1]],
                [mapBbox.value[0][0], mapBbox.value[0][1]]
            ]
        });
    }

    if (fitBounds && mapBbox.value) {
        map.fitBounds(mapBbox.value, { padding: 20 });
        // ensure map redraw after changing bounds
        nextTick(() => { try { map.resize(); } catch (e) { void e; /* swallow resize errors */ } });
    } else {
        // on normal updates also ensure correct sizing in case layout changed
        nextTick(() => { try { map.resize(); } catch (e) { void e; /* swallow resize errors */ } });
    }

    if (mapMarker) {
        updatePopupContent();
    }
}
watch(currentDateIDX, () => updateMap());

function onMapClick(e) {
    if (!isSelectionMode.value) return;

    const { lat, lng } = e.lngLat.wrap();

    const [minLat, maxLat] = [bbox.value[0][0], bbox.value[1][0]];
    const [minLng, maxLng] = [bbox.value[0][1], bbox.value[1][1]];
    const latCellSize = (maxLat - minLat) / allRasterData.value[0].length;
    const lngCellSize = (maxLng - minLng) / allRasterData.value[0][0].length;
    const row = Math.floor((maxLat - lat) / latCellSize);
    const col = Math.floor((lng - minLng) / lngCellSize);
    const boundedRow = Math.max(0, Math.min(allRasterData.value[0].length - 1, row));
    const boundedCol = Math.max(0, Math.min(allRasterData.value[0][0].length - 1, col));

    selectedPosition.value = { lat, lng, boundedRow, boundedCol };

    if (mapMarker) {
        mapMarker.remove();
    }

    const popup = new mapboxgl.Popup({ closeButton: false, offset: 25 })
        .setHTML(getPopupHTML());

    mapMarker = new mapboxgl.Marker().setLngLat(e.lngLat).setPopup(popup).addTo(map);
    mapMarker.togglePopup();

    popup.on('close', () => {
        closeGraph();
        if (mapMarker) {
            mapMarker.remove();
            mapMarker = null;
        }
        selectedPosition.value = null;
    });

    openGraph();
    isSelectionMode.value = false;
}

function toggleSelectionMode() {
    isSelectionMode.value = !isSelectionMode.value;
    if (mapContainer.value) {
        mapContainer.value.style.cursor = isSelectionMode.value ? 'crosshair' : '';
    }
}

function updatePopupContent() {
    if (mapMarker && mapMarker.getPopup()) {
        mapMarker.getPopup().setHTML(getPopupHTML());
    }
}
function getPopupHTML() {
    const value = positionDataValue.value;
    return `
      <div class="popup-content">
        <strong class="popup-title">${OPTION_CLIMA_VAR[selection.climateVar]}</strong>
        <p class="popup-date">${formattedCurrentDate.value}</p>
        <p class="popup-value">
            Value: <span class="popup-number">${value !== null && value !== undefined ? value.toFixed(1) : 'N/A'}</span>
            <span class="popup-unit">${currentUnit.value}</span>
        </p>
      </div>`;
}

// --- RASTER IMAGE CREATION ---
function createRasterImage(dataIndex) {
    const data = allRasterData.value[dataIndex];
    if (!data || data.length === 0 || data[0].length === 0) return 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs='; // Transparent pixel
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const height = data.length;
    const width = data[0].length;
    canvas.width = width;
    canvas.height = height;

    const imageData = ctx.createImageData(width, height);
    const pixels = imageData.data;

    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const value = data[y][x];
            const color = getColor(value, selection.climateVar);
            const index = (y * width + x) * 4;
            pixels[index] = color[0];     // R
            pixels[index + 1] = color[1]; // G
            pixels[index + 2] = color[2]; // B
            pixels[index + 3] = color[3]; // A
        }
    }
    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL();
}

function getColor(value, parameterName) {
    const settings = COLOR_SETTINGS[parameterName];
    if (!settings) return [0, 0, 0, 0];
    const colorStops = settings.color_palette;
    if (isNaN(value) || value === null) return [0, 0, 0, 0];

    const [minValue, maxValue] = settings.min_max;

    const hexToRgb = (hex) => [parseInt(hex.substring(1, 3), 16), parseInt(hex.substring(3, 5), 16), parseInt(hex.substring(5, 7), 16)];

    if (value <= minValue) return [...hexToRgb(colorStops[0]), 255];
    if (value >= maxValue) return [...hexToRgb(colorStops[colorStops.length - 1]), 255];

    const normalizedValue = (value - minValue) / (maxValue - minValue);
    const segmentSize = 1 / (colorStops.length - 1);
    const segmentIndex = Math.min(Math.floor(normalizedValue / segmentSize), colorStops.length - 2);

    const startColor = hexToRgb(colorStops[segmentIndex]);
    const endColor = hexToRgb(colorStops[segmentIndex + 1]);
    const localFactor = (normalizedValue - segmentIndex * segmentSize) / segmentSize;

    const interpolate = (start, end, factor) => Math.round(start + factor * (end - start));

    return [
        interpolate(startColor[0], endColor[0], localFactor),
        interpolate(startColor[1], endColor[1], localFactor),
        interpolate(startColor[2], endColor[2], localFactor),
        255
    ];
}


// --- GRAPH LOGIC ---
function initializeChart() {
    if (!chartCanvas.value) return;
    const ctx = chartCanvas.value.getContext('2d');
    chart = new Chart(ctx, {
        type: 'line',
        data: { datasets: [{ label: "Measurements", data: [], borderColor: "#2563eb", backgroundColor: "rgba(37, 99, 235, 0.1)", borderWidth: 2, fill: true, tension: 0.4, pointRadius: 0 }] },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 0 },
            interaction: { intersect: false, mode: 'nearest', axis: 'x' },
            scales: {
                x: {
                    type: "time",
                    time: { unit: "year", tooltipFormat: "dd.MM.yyyy", displayFormats: { year: 'yyyy' } },
                    title: { display: true, text: "Years" }
                },
                y: {
                    beginAtZero: false,
                    title: { display: true, text: "Value" }
                }
            },
            plugins: {
                legend: { display: false },
                tooltip: {
                    displayColors: false,
                    callbacks: {
                        title: (context) => DateTime.fromMillis(context[0].parsed.x).toFormat('LLLL yyyy'),
                        label: (context) => `Value: ${context.parsed.y.toFixed(2)} ${currentUnit.value}`
                    }
                }
            }
        }
    });

    chartCanvas.value.addEventListener('mousemove', (event) => {
        const activePoints = chart.getElementsAtEventForMode(event, 'nearest', { intersect: false }, true);
        if (activePoints.length) {
            const pointIndex = activePoints[0].index;
            const hoveredTimestamp = timeSeriesData.value[pointIndex].x;
            const originalIndex = timeStamps.value.findIndex(item => new Date(item.date).getTime() === hoveredTimestamp);
            if (originalIndex !== -1) {
                currentDateIDX.value = originalIndex;
            }
        }
    });
}

function openGraph() {
    isGraphOpen.value = true;
    nextTick(() => {
        if (!chart) initializeChart();
        updateGraph();
        if (chart) chart.resize();
    });
}

function closeGraph() { isGraphOpen.value = false; }

function updateGraph() {
    if (!chart || !isGraphOpen.value) return;
    chart.data.datasets[0].data = timeSeriesData.value;
    chart.options.scales.y.title.text = OPTION_CLIMA_VAR[selection.climateVar] || 'Value';
    chart.update('none');
}

// --- LIFECYCLE HOOK ---
onMounted(() => {
    // attach window resize handler so the map resizes when the layout changes
    window.addEventListener('resize', handleWindowResize);
    // listen for parent tab activation requests
    window.addEventListener('map-resize', handleExternalMapResize);
    loadInitialData();
});

// add these watchers / small guards near the other watchers and handlers (after handleGlobalModelChange/handleRegionalModelChange)
watch(() => selection.global, () => {
    // ensure regional selection stays valid for chosen global model
    const regs = availableRegionalModels.value;
    if (!regs.includes(selection.regional)) {
        selection.regional = regs.length > 0 ? regs[0] : '';
    }
    // update scenarios to reflect new global/regional combination and keep scenario valid
    available.scenarios = availableRcpScenarios.value;
    if (!available.scenarios.includes(selection.scenario)) {
        selection.scenario = available.scenarios.length > 0 ? available.scenarios[0] : '';
    }
    // refresh data for the new valid combination
    updateData();
});

watch(() => selection.regional, () => {
    // ensure scenario stays valid for chosen global+regional
    available.scenarios = availableRcpScenarios.value;
    if (!available.scenarios.includes(selection.scenario)) {
        selection.scenario = available.scenarios.length > 0 ? available.scenarios[0] : '';
    }
    updateData();
});
</script>

<style scoped>
.sidebar {
    border-radius: 25px;
    max-height: 90vh;
    overflow-y: auto;
}

.info-panel {
    border-radius: 25px;
}

#map-container {
    position: relative;
    width: 100%;
    height: 70vh;
}

#map {
    width: 100%;
    height: 100%;
}

.visualization-controls {
    background-color: rgba(255, 255, 255, 0.9);
    border-radius: 10px;
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
    margin-top: 1rem;
}

#legend {
    width: 100%;
    box-sizing: border-box;
}

.legend-gradient {
    height: 20px;
    border: 1px solid #dee2e6;
    margin-bottom: 0.5rem;
    border-radius: 0.25rem;
}

.graph-container {
    position: fixed;
    bottom: 20px;
    left: 25%;
    /* Adjusted to be next to the sidebar */
    width: 70%;
    height: 40%;
    background-color: rgba(255, 255, 255, 0.95);
    padding: 20px;
    border-radius: 10px;
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
    transition: all 0.3s ease;
    z-index: 1000;
    transform: translateY(100%);
    opacity: 0;
    pointer-events: none;
}

.graph-container.open {
    transform: translateY(0);
    opacity: 1;
    pointer-events: auto;
}

.chart-container {
    position: relative;
    width: 100%;
    height: 100%;
}

.close-btn {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 30px;
    height: 30px;
    background: #dc3545;
    border: none;
    border-radius: 50%;
    font-size: 1.5rem;
    font-weight: bold;
    color: #fff;
    text-align: center;
    line-height: 28px;
    cursor: pointer;
    z-index: 1100;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.close-btn:hover {
    background: #c82333;
}

.loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
}

.loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #0d6efd;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.loading-text {
    margin-top: 0.5rem;
    color: #6c757d;
    font-size: 0.85rem;
}

.mapboxgl-popup-content {
    background: rgba(255, 255, 255, 0.9) !important;
    backdrop-filter: blur(6px);
    border-radius: 10px !important;
    padding: 0 !important;
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
}

.popup-content {
    min-width: 180px;
    padding: 8px 12px;
}

.popup-title {
    font-size: 1rem;
    font-weight: 600;
}

.popup-date {
    font-size: 0.75rem;
    color: #64748b;
}

.popup-value {
    font-size: 0.75rem;
    color: #334155;
    margin-top: 4px;
}

.popup-number {
    font-weight: 600;
    color: #2563eb;
    font-size: 1rem;
}

.popup-unit {
    color: #64748b;
}
</style>